import Cookies from "js-cookie";
import store from "../config/configureStore";
import { getSettings, clearSettings } from "./ConfigReducer";
import { notify } from "./NotifierReducer";
import { setCircularLoading } from "./UiReducer";
import { getUserSettings } from "./UserSettingsReducer";
import {
  checkForNewVersion,
  updateApp,
  updateIfNewVersionAvail,
} from "./AppInfoReducer";
import { messages } from "../utils/constants";
import { getDictionaries } from "./DictionaryReducer";

export const USER_SET_AUTHENTICATED = "USER/SET_AUTHENTICATED";
export const USER_SET_DATA = "USER/SET_DATA";
export const USER_SET_USER_UNIT = "USER/USER_SET_USER_UNIT";
export const USER_SET_USER_UNITS = "USER/SET_USER_UNITS";
export const USER_CLEAR_DATA = "USER/USER_CLEAR_DATA";
export const USER_SET_PERMISSION = "USER/USER_SET_PERMISSION";
export const PASSWORD_FIELD_SHOULD_RESET = "PASSWORD_FIELD_SHOULD_RESET";
export const USER_SET_OTP_REQUIRED = "USER/SET_OTP_REQUIRED";
export const USER_SET_AGENCY = "USER/SET_AGENCY";
let logOutSent = false;

export const submitLoginForm = (username, password, otp = null) => {
  const client = store.store.getState().websocket.websocket;
  return async (dispatch) => {
    try {
      dispatch(setCircularLoading(true));
      const authPayload = {
        AppName: "AMS",
        strategy: "local",
        username: username,
        password: password,
      };
      if (otp) {
        authPayload.otp = otp;
      }
      const authResponse = await client.authenticate(authPayload);
      logOutSent = false;
      dispatch({
        type: USER_SET_AGENCY,
        payload:
          authResponse.user.AgencyID !== null ? authResponse.user.AgencyID : "",
      });
      dispatch({ type: USER_SET_DATA, payload: authResponse });
      dispatch({ type: USER_SET_AUTHENTICATED, payload: true });
      dispatch({ type: USER_SET_OTP_REQUIRED, payload: false });
      dispatch(getSettings());
      dispatch(getUserSettings(authResponse?.user?.ptsUserID));
      dispatch(updateIfNewVersionAvail());
      //dispatch(updateZones());
    } catch (error) {
      const { code, message } = error;
      switch (code) {
        case 408:
          dispatch(
            notify(
              "It takes too long to connect to the server. Please check your connection and try again.",
              "warning"
            )
          );
          break;
        case 401:
          if (message === messages.otpSent) {
            dispatch({ type: USER_SET_OTP_REQUIRED, payload: true });
            dispatch(notify("OTP required. Please check your email.", "info"));
          } else if (message) {
            dispatch({ type: PASSWORD_FIELD_SHOULD_RESET, payload: true });
            dispatch({ type: USER_SET_OTP_REQUIRED, payload: false });
            dispatch(notify(message, "error"));
          } else {
            dispatch(notify("Login failed. Please try again.", "error"));
            dispatch({ type: USER_SET_OTP_REQUIRED, payload: false });
          }
          break;
        default:
          dispatch(
            notify(
              `Unknown error ${code}. Please check your connection and try again.`,
              "error"
            )
          );
          dispatch({ type: USER_SET_OTP_REQUIRED, payload: false });
      }
    } finally {
      dispatch(setCircularLoading(false));
    }
  };
};

export const sendPassResetLink = (email) => {
  const client = store.store.getState().websocket.websocket;
  if (!client) {
    return;
  }
  const service = client.service("pass-reset");
  // service.timeout = 40000;

  return service.create({
    action: "pass-reset",
    email,
    ResetMethod: "email",
    AppName: "AMS",
    RedirectTo: window.__RUNTIME_CONFIG__.REACT_APP_PUBLIC_URL + "/Reset-password",
  });
};

export const setUserAuthenticated = (state) => {
  const client = store.store.getState().websocket.websocket;
  return (dispatch) => {
    if (client) {
      if (state === false) {
        dispatch(logOut());
      } else {
        dispatch({ type: USER_SET_AUTHENTICATED, payload: true });
      }
    }
  };
};

export const logOut = () => async (dispatch) => {
  if (logOutSent) return;
  logOutSent = true;
  try {
    const state = store.store.getState();
    const { appInfo } = state;
    const client = state.websocket.websocket;
    client
      .service("authenticate")
      .remove("AMS")
      .catch((e) => {});
    client.logout();

    window.localStorage.removeItem("auth");
    dispatch({ type: USER_CLEAR_DATA });
    dispatch({ type: USER_SET_AUTHENTICATED, payload: false });
    dispatch(clearSettings());

    const { registration } = appInfo;
    if (registration) {
      updateApp(registration);
    } else {
      const { buildNo } = appInfo;
      const newVer = await checkForNewVersion();
      if (buildNo && buildNo !== newVer?.build) window.location.reload();
    }
  } catch (err) {
    console.log(err);
  }

  // dispatch(setUserData(null));
};

export const setUserData = (data) => {
  return (dispatch) => {
    dispatch({ type: USER_SET_DATA, payload: data });
  };
};

export const validateResetPassHash = (hash) => {
  const client = store.store.getState().websocket.websocket;
  if (!client) {
    return;
  }
  const service = client.service("pass-reset");
  // service.timeout = 40000;
  return service.get(hash);
};

export const resetPass = (hash, newPass) => {
  const client = store.store.getState().websocket.websocket;
  if (!client) {
    return;
  }
  const service = client.service("pass-reset");
  // service.timeout = 40000;
  return service.patch(hash, { newPass, AppName: "AMS" });
};

export const setPasswordFieldShouldReset = (flag = false) => {
  return (dispatch) => {
    dispatch({ type: PASSWORD_FIELD_SHOULD_RESET, payload: flag });
  };
};

const defaultState = {
  isAuthenticated: false,
  userData: null,
  permissions: [],
  otpRequired: false,
  userAgency: "",
};

export const authorizeExternalApp = () => {
  const isProduction = window.__RUNTIME_CONFIG__.NODE_ENV !== "development";
  const auth = window.localStorage.getItem("auth");
  const domain = window.location.hostname;
  const parentDomain = domain.substr(domain.indexOf(".") + 1);
  Cookies.set("auth", auth, {
    expires: new Date(new Date().getTime() + 2500),
    path: "/",
    domain: parentDomain,
    secure: isProduction,
  });
};

export const setUserAgency = (agency) => {
  return (dispatch) => {
    dispatch({ type: USER_SET_AGENCY, payload: agency });
    dispatch(getDictionaries("ams"));
  };
};

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case USER_SET_AUTHENTICATED:
      return {
        ...state,
        isAuthenticated: action.payload,
      };
    case USER_SET_DATA:
      return {
        ...state,
        userData: action.payload,
      };
    case USER_CLEAR_DATA:
      return defaultState;
    case USER_SET_PERMISSION:
      return {
        ...state,
        userData: {
          ...state.userData,
          user: {
            ...state.userData.user,
            Permissions: JSON.stringify(action.payload),
          },
        },
      };
    case PASSWORD_FIELD_SHOULD_RESET:
      return {
        ...state,
        passwordFieldShouldReset: action.payload,
      };
    case USER_SET_OTP_REQUIRED:
      return {
        ...state,
        otpRequired: action.payload,
      };
    case USER_SET_AGENCY:
      return {
        ...state,
        userAgency: action.payload,
      };
    default:
      break;
  }
  return state;
}
